import React, { PureComponent } from 'react';
import { Marker } from 'react-google-maps';
import {
  Button,
  Header,
  Segment,
  Message,
  Modal,
  Card,
  Icon,
  Checkbox,
  Statistic,
  Divider,
} from 'semantic-ui-react';
import './AutomaticPlannerScreen.css';
import maxBy from 'lodash/maxBy';
import sumBy from 'lodash/sumBy';
import floor from 'lodash/floor';
import isEqual from 'lodash/isEqual';
import clone from 'lodash/clone';
import findIndex from 'lodash/findIndex';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import debounce from 'lodash/debounce';
import keyBy from 'lodash/keyBy';
import uniqWith from 'lodash/uniqWith';
import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';
import last from 'lodash/last';
import difference from 'lodash/difference';
import mean from 'lodash/mean';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import moment from 'moment-timezone';
import Chance from 'chance';
import Promise from 'bluebird';
import memoize from 'memoize-one';
import geolib from 'geolib';

import Map from '../../../components/common/Map';
import PlannerVehiclesManager from '../../../components/planner/PlannerVehiclesManager';
import TravelRequirementsManager from '../../../components/planner/TravelRequirementsManager';
import TravelRequirementEditor from '../../../components/planner/TravelRequirementEditor';
import RouteDisplay from '../../../components/planner/RouteDisplay';
import RoutesSaver from '../../../components/planner/RoutesSaver';
import RoutesImporter from '../../../components/planner/RoutesImporter';
import PlannerOptions from '../../../components/planner/PlannerOptions';
import VehiclesMap from '../../../components/liveMap/VehiclesMap';
import { routesToTrips } from '../../../components/planner/RoutesSaver/RoutesSaver';
import withi18n from 'weego-common/src/hoc/i18n';

import zones from './zones.js';
import getNextDateOfDay from '../../../utils/date/getNextDateOfDay';

const busIconPath = `M219.5,0 C332.994516,-2.08486044e-14 425,92.0054839 425,205.5 C425,303.170707 356.861492,384.926812 265.52714,405.825653 L225.789848,462.622694 C221.040945,469.410703 211.688429,471.063725 204.90042,466.314821 C203.505776,465.339126 202.287366,464.133183 201.297378,462.748648 L160.803131,406.12537 C68.8009169,385.719308 1.20192117e-14,303.644312 0,205.5 C-1.38990696e-14,92.0054839 92.0054839,2.08486044e-14 205.5,0 L219.5,0 Z M250.500007,309.5 L175.500007,309.5 L175.500007,334.5 C175.500007,341.403559 169.903573,347 163.000007,347 L150.500007,347 C143.596454,347 138.000007,341.403559 138.000007,334.5 L138.000007,302.8125 C122.522705,293.882634 112.990838,277.368673 113.000007,259.5 L113.000007,147 C113.000007,119.385763 135.385776,97 163.000007,97 L263.000007,97 C290.614251,97 313.000007,119.385763 313.000007,147 L313.000007,259.5 C313.000007,278 302.950013,294.1625 288.000007,302.8125 L288.000007,334.5 C288.000007,341.403559 282.403573,347 275.500007,347 L263.000007,347 C256.096454,347 250.500007,341.403559 250.500007,334.5 L250.500007,309.5 Z M288.000013,209.5 L288.000013,147 C288.000013,133.192881 276.807132,122 263.000013,122 L163.000013,122 C149.192894,122 138.000013,133.192881 138.000013,147 L138.000013,209.5 L288.000013,209.5 Z M288.000013,234.5 L138.000013,234.5 L138.000013,259.5 C138.000013,273.307119 149.192894,284.5 163.000013,284.5 L263.000013,284.5 C276.807132,284.5 288.000013,273.307119 288.000013,259.5 L288.000013,234.5 Z M163.000013,134.5 C169.903573,134.5 175.500013,140.096441 175.500013,147 L175.500013,184.5 C175.500013,191.403559 169.903573,197 163.000013,197 C156.096454,197 150.500013,191.403559 150.500013,184.5 L150.500013,147 C150.500013,140.096441 156.096454,134.5 163.000013,134.5 Z M163.000013,272 C156.096454,272 150.500013,266.403559 150.500013,259.5 C150.500013,252.596441 156.096454,247 163.000013,247 C169.903573,247 175.500013,252.596441 175.500013,259.5 C175.500013,266.403559 169.903573,272 163.000013,272 Z M263.000013,272 C256.096454,272 250.500013,266.403559 250.500013,259.5 C250.500013,252.596441 256.096454,247 263.000013,247 C269.903573,247 275.500013,252.596441 275.500013,259.5 C275.500013,266.403559 269.903573,272 263.000013,272 Z`;

const chance = new Chance();
const mapRef = React.createRef();

class AutomaticPlannerScreen extends PureComponent {
  state = {
    depot: null,
    travelRequirements: [], // Demands List
    selectedRequirement: null,
    addingTravelRequirement: false,
    mapCenter: null,
    vehicles: [],
    generating: false,
    routes: [],
    selectedDay: 1,
    focusedTrip: null,
    selectedTrips: [], // Trips list
    unfulfilledDemandIds: [], // Demands Ids list that are not fulfilled
    error: null,
    saveModalOpen: false,
    importModalOpen: false,
    useRoutesAsInitial: false,
    unfulfilledStops: [],
    mapViewMode: 'DEMANDS',
    includePlannedDemands: false,
    options: {
      selectedDay: 1,
      timeMapKey: '',
      defaultTimeMultiplier: 1,
      maxTimeMultiplier: 3,
      ignoreVehicleDepots: false,
      splitMultiPassengerDemands: false,
      selectedTimeRange: [0, 15],
      maxTime: 3600,
      maxTimeSoft: 2700,
      maxDelay: 15 * 60,
      zones,
    },
    statistics: {},
    isStatsPanelOpen: false,
  };

  componentDidMount() {
    document.title = 'Planificateur';
  }

  updateVehicles = vehicles => {
    this.setState({
      vehicles,
    });
  };

  updateTravelRequirements = travelRequirements => {
    this.setState({
      travelRequirements,
    });
  };

  selectRequirement = requirement => {
    this.setState({
      selectedRequirement: requirement,
      mapViewMode: 'DEMANDS',
    });
  };

  dismissSelectedRequirement = () => {
    this.setState({
      selectedRequirement: null,
    });
  };

  updateSelectedRequirement = updatedRequirement => {
    this.setState({
      travelRequirements: this.state.travelRequirements.map(r =>
        r.id === updatedRequirement.id ? updatedRequirement : r,
      ),
    });
  };

  focusTrip = trip => {
    const { focusTrips } = this.props;
    this.setState({
      focusedTrip: trip,
      mapViewMode: 'TRIPS',
    });
    focusTrips([trip], true);
  };

  dismissFocusedTrip = () => {
    this.setState({
      focusedTrip: null,
    });
  };

  selectTrip = (trip, selected = true) => {
    if (selected) {
      this.setState({
        selectedTrips: uniq(this.state.selectedTrips.concat(trip)),
      });
    } else {
      this.setState({
        selectedTrips: this.state.selectedTrips.filter(t => t.id !== trip.id),
      });
    }
  };

  vehiclesById = memoize(vehicles => keyBy(vehicles, 'id'));

  importRoutes = routes => {
    this.setState({
      routes: routes.map(route => ({
        ...route,
        color: this.vehiclesById(this.state.vehicles)[route.vehicle.id].color,
      })),
      useRoutesAsInitial: true,
      importModalOpen: false,
    });
  };

  getDistanceMatrix = async options => {
    console.log(
      `Getting distance matrix between ${options.origins.length} and ${options.destinations.length} stops`,
    );
    let { travelRequirements, departureTime } = options;
    if (!departureTime) {
      // If the user did not select a departure time
      // we take the mean of all demand times
      departureTime = moment.unix(
        mean(
          travelRequirements.map(requirement =>
            requirement.departure.time || requirement.arrival.time
              ? moment(requirement.departure.time || requirement.arrival.time)
                  .set('day', this.state.options.selectedDay)
                  .unix()
              : null,
          ),
        ),
      );

      // In case the selected day for this week has already passed, we
      // use the same day but next week
      if (departureTime.isBefore(moment())) {
        departureTime.add(1, 'week');
      }
    }

    const response = await fetch(
      `https://api.tomtom.com/routing/1/matrix/json?key=ZyHeo23dxQWcEATKYqz10Vx14qDt6Jjl&redirectMode=manual&traffic=true&departAt=${departureTime.toISOString()}&travelMode=car`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          origins: options.origins.map(latLng => ({
            point: latLngToCoordinates(latLng),
          })),
          destinations: options.destinations.map(latLng => ({
            point: latLngToCoordinates(latLng),
          })),
        }),
      },
    );
    if (!response.ok) {
      if (response.body) {
        const body = await response.json();
        throw new Error(
          body.message ||
            'Unknown error when fetching distance matrix response',
        );
      } else {
        throw new Error('Network error');
      }
    }
    if (response.status === 202) {
      while (true) {
        const matrixLocation = response.headers.get('Location');
        const matrixLocationResponse = await fetch(
          `https://api.tomtom.com${matrixLocation}`,
        );
        if (!matrixLocationResponse.ok) {
          if (matrixLocationResponse.body) {
            const body = await matrixLocationResponse.json();
            throw new Error(
              body.message ||
                'Unknown error when fetching distance matrix response',
            );
          } else {
            throw new Error('Network error');
          }
        }
        if (matrixLocationResponse.status === 202) {
          continue;
        }
        const matrixBody = await matrixLocationResponse.json();
        return matrixBody;
      }
    } else {
      const body = await response.json();
      return body;
    }
  };

  resetTmCache = () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      console.log(key);
      if (key.startsWith('PLANNER::TIME_MAP::')) {
        keysToRemove.push(key);
      }
    }
    for (let key of keysToRemove) {
      localStorage.removeItem(key);
    }
  };

  generateRoutes = async () => {
    try {
      const { t } = this.props;
      let {
        options,
        travelRequirements,
        vehicles,
        depot,
        routes,
        useRoutesAsInitial,
      } = this.state;
      this.setState({
        generating: true,
        routes: [],
        unfulfilledStops: [],
        unfulfilledDemandIds: [],
      });
      const {
        defaultTimeMultiplier,
        selectedDay,
        zones,
        maxTimeMultiplier,
        overtimePenaltyCoef,
        overtimePenalty,
        overdistancePenalty,
        overdistancePenaltyCoef,
        distanceCostCoefficient,
        // ignoreVehicleDepots,
        timeLimit,
        maxTime,
        maxTimeSoft,
        maxDelay,
      } = options;

      vehicles = vehicles.filter(v => !v.disabled);

      const defaultDepot = {
        name: 'Dépôt par défaut',
        coords: depot.coords,
      };

      const requirementGroups = groupBy(
        travelRequirements,
        r => r.departure.time || r.arrival.time,
      );

      const mergedGroups = reduce(
        requirementGroups,
        (accumulator, group) => {
          const lastGroup = last(accumulator);
          if (
            lastGroup &&
            lastGroup.length < 20000 &&
            group.length < 20000 &&
            lastGroup.length + group.length < 20000
          ) {
            lastGroup.push(...group);
            return accumulator;
          } else {
            accumulator.push(group);
            return accumulator;
          }
        },
        [],
      );

      const stopDistance = (stop1, stop2) =>
        geolib.getDistance(
          { latitude: stop1.coords.lat, longitude: stop1.coords.lng },
          { latitude: stop2.coords.lat, longitude: stop2.coords.lng },
        );

      await Promise.map(mergedGroups, async travelRequirementsGroup => {
        const depots = vehicles.map(vehicle => vehicle.depot || defaultDepot);

        let allStops = uniqWith(
          uniq(depots).concat(
            flatten(
              travelRequirementsGroup.map(requirement => [
                requirement.departure,
                requirement.arrival,
              ]),
            ),
          ),
          (stop1, stop2) => {
            if (stopDistance(stop1, stop2) < 300) {
              return true;
            }
          },
        );

        console.log(
          'Calculating time matrix for',
          allStops.length,
          'stops',
          allStops,
        );

        const dayTravelRequirements = travelRequirementsGroup;
        if (!dayTravelRequirements.length) {
          return;
        }
        const dayAllStops = uniq(depots).concat(
          flatten(
            dayTravelRequirements.map(requirement => [
              requirement.departure,
              requirement.arrival,
            ]),
          ),
        );

        let initialRoutes = null;
        if (useRoutesAsInitial) {
          const dayRoutes = routes;
          const routesByVehicleId = keyBy(dayRoutes, 'vehicle.id');
          initialRoutes = vehicles
            .map(vehicle => {
              const assignedRoute = routesByVehicleId[vehicle.id];
              if (assignedRoute) {
                const route = assignedRoute.stops
                  .map(stop => {
                    if (!stop.id) {
                      return null;
                    }
                    const stopIndex = findIndex(
                      dayAllStops,
                      s => s.id === stop.id,
                    );
                    return stopIndex !== -1 ? stopIndex : null;
                  })
                  .filter(v => v !== null);
                return route;
              } else {
                return [];
              }
            })
            .filter(v => v !== null);
        }

        const startOfDay = moment().startOf('day');
        const startOfDayUnix = startOfDay.unix();

        const makeStopTimeWindow = (stop, isReturn) =>
          stop.time
            ? isReturn
              ? [
                  moment(stop.time).unix() - startOfDayUnix,
                  moment(stop.time)
                    .add(maxDelay / 60, 'minutes')
                    .unix() - startOfDayUnix,
                ]
              : [
                  moment(stop.time)
                    .subtract(maxDelay / 60, 'minutes')
                    .unix() - startOfDayUnix,
                  moment(stop.time).add(0, 'minutes').unix() - startOfDayUnix,
                ]
            : null;

        const timeWindows = uniq(depots)
          .map(makeStopTimeWindow)
          .concat(
            flatten(
              dayTravelRequirements.map(requirement => {
                const { departure, arrival } = requirement;
                if (arrival.time && !departure.time) {
                  const arrivalTimeWindow = makeStopTimeWindow(arrival, false);
                  return [null, arrivalTimeWindow];
                }
                if (departure.time && !arrival.time) {
                  const departureTimeWindow = makeStopTimeWindow(
                    departure,
                    true,
                  );
                  return [departureTimeWindow, null];
                }
                return [
                  makeStopTimeWindow(departure, false),
                  makeStopTimeWindow(arrival, false),
                ];
              }),
            ),
          );
        const pickupDeliveries = dayTravelRequirements.map(requirement => [
          dayAllStops.indexOf(requirement.departure),
          dayAllStops.indexOf(requirement.arrival),
        ]);

        const dropPenalty = new Array(dayAllStops.length);
        dropPenalty.fill(400 * 1000 * 1000);
        const demands = dayAllStops.map(stop => stop.demand || 0);
        const numVehicles = vehicles.length;
        const vehicleCapacities = vehicles.map(vehicle => vehicle.capacity);
        const vehicleCosts = vehicles.map(
          vehicle => parseInt(vehicle.cost) || 1,
        );
        const starts = vehicles.map(vehicle =>
          vehicle.depot
            ? dayAllStops.indexOf(vehicle.depot)
            : dayAllStops.indexOf(defaultDepot),
        );
        const ends = starts;

        const requestData = {
          time_windows: timeWindows,
          pickups_deliveries: pickupDeliveries,
          drop_penalty: dropPenalty,
          split_stop_penalty: 50 * 1000 * 1,
          demands: demands,
          num_vehicles: numVehicles,
          vehicle_capacities: vehicleCapacities,
          vehicle_costs: vehicleCosts,
          starts,
          ends,
          initial_routes: initialRoutes,
          stops: dayAllStops,
          max_time_multiplier: parseFloat(maxTimeMultiplier),
          max_time: parseInt(maxTime),
          max_time_soft: parseInt(maxTimeSoft),
          overtime_penalty_coef: parseFloat(overtimePenaltyCoef),
          overdistance_penalty_coef: parseFloat(overdistancePenaltyCoef),
          overdistance_penalty: parseFloat(overdistancePenalty),
          overtime_penalty: parseFloat(overtimePenalty),
          distance_cost_coefficient: parseFloat(distanceCostCoefficient),
          time_limit: parseInt(timeLimit),
        };
        const pdTw = ([p, d]) => {
          const time = distanceMatrix[p][d];
          let maxTime = Math.min(
            Math.floor(time * requestData.max_time_multiplier),
            requestData.max_time,
          );
          maxTime = Math.max(maxTime, 20 * 60, maxDelay);
          const timeWindows = [
            requestData.time_windows[p]
              ? requestData.time_windows[p].map(
                  seconds => startOfDayUnix + seconds,
                )
              : [
                  requestData.time_windows[d][1] - maxTime + startOfDayUnix,
                  requestData.time_windows[d][1] + startOfDayUnix,
                ],
            requestData.time_windows[d]
              ? requestData.time_windows[d].map(
                  seconds => startOfDayUnix + seconds,
                )
              : [
                  requestData.time_windows[p][0] + startOfDayUnix,
                  requestData.time_windows[p][0] + maxTime + startOfDayUnix,
                ],
          ];

          return timeWindows;
        };
        const allStopsCoordsString = dayAllStops
          .map(stop => `${stop.coords.lng},${stop.coords.lat}`)
          .join(';');
          // HACK: avoid having 414 error when too many stops
          // http://15.236.186.213:5000/table/v1/driving/${allStopsCoordsString}
        const dmResponse = await fetch(`https://osrm.weegolife.com/table/v1/driving/${allStopsCoordsString}`);
        if (!dmResponse.ok) {
          const error = dmResponse.body
            ? await dmResponse.json()
            : { message: 'Network error' };
          throw error;
        }
        const dmBody = await dmResponse.json();
        const distanceMatrix = dmBody.durations.map((row, rowIndex) =>
          row.map((value, columnIndex) => {
            if (value === null) {
              throw new Error(
                t(
                  'Valeur invalide dans la matrice de distance. Vérifier que les réservations et dépôts se trouvent dans des pays supportés',
                ),
              );
            }
            const stop1 = dayAllStops[rowIndex];
            const stop2 = dayAllStops[columnIndex];
            const getZoneForCoords = coords =>
              zones.find(zone =>
                geolib.isPointInside(
                  latLngToCoordinates(coords),
                  zone.bounds[0].map(point => ({
                    latitude: point[1],
                    longitude: point[0],
                  })),
                ),
              );
            const stop1Zone = getZoneForCoords(stop1.coords);
            const stop2Zone = getZoneForCoords(stop2.coords);
            let timeMultiplier = defaultTimeMultiplier;
            if (!stop1Zone || !stop2Zone) {
              timeMultiplier = defaultTimeMultiplier;
            } else if (stop1Zone === stop2Zone) {
              timeMultiplier = stop1Zone.timeMultiplier;
            } else {
              timeMultiplier =
                (stop1Zone.timeMultiplier + stop2Zone.timeMultiplier) / 2;
            }
            console.log(
              'Time multiplier between',
              stop1.name,
              'and',
              stop2.name,
              'is',
              timeMultiplier,
            );
            return Math.ceil(value * (timeMultiplier || defaultTimeMultiplier));
          }),
        );
        const vroomRequestData = {
          matrix: distanceMatrix,
          vehicles: requestData.starts.map((startIndex, vehicleIndex) => ({
            id: vehicleIndex,
            start_index: startIndex,
            capacity: [requestData.vehicle_capacities[vehicleIndex]],
            time_window: vehicles[vehicleIndex].timeWindow
              ? vehicles[vehicleIndex].timeWindow.map(
                  seconds => seconds + startOfDayUnix,
                )
              : undefined,
            // vehicles[vehicleIndex].id === '6010fa51edeafe2b70a9f0a4'
            //   ? [18 * 60 * 60 + startOfDayUnix, startOfDayUnix + 24 * 60 * 60]
            //   : undefined,
          })),
          shipments: requestData.pickups_deliveries.map(
            ([pickupIndex, deliveryIndex]) => ({
              pickup: {
                id: pickupIndex,
                location_index: pickupIndex,
                time_windows: [pdTw([pickupIndex, deliveryIndex])[0]],
                description: `${requestData.stops[pickupIndex].name} ${
                  requestData.stops[pickupIndex].time || ''
                }`,
              },
              delivery: {
                id: deliveryIndex,
                location_index: deliveryIndex,
                time_windows: [pdTw([pickupIndex, deliveryIndex])[1]],
                description: `${requestData.stops[deliveryIndex].name} ${
                  requestData.stops[deliveryIndex].time || ''
                }`,
              },
              amount: [1],
            }),
          ),
        };

        // HACK: avoid having 414 error when too many stops use http://15.188.65.251:3001/vroom
        const vroomResponse = await fetch('https://vroom.weegolife.com/vroom', {
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(vroomRequestData),
          method: 'POST',
        });
        if (!vroomResponse.ok) {
          const error = vroomResponse.body
            ? await vroomResponse.json()
            : { message: 'Network error' };
          throw error;
        }
        const vroomData = await vroomResponse.json();
        const adaptedVroomRoutes = requestData.starts.map(
          (startIndex, vehicleIndex) => {
            const route = vroomData.routes.find(
              r => r.vehicle === vehicleIndex,
            );
            if (!route) {
              return [
                {
                  index: startIndex,
                  min_time: 0,
                  max_time: 0,
                },
                {
                  index: requestData.ends[vehicleIndex],
                  min_time: 86399,
                  max_time: 86399,
                },
              ];
            }
            const sortedRouteSteps = sortBy(
              route.steps,
              step =>
                step.arrival +
                step.waiting_time +
                (step.type === 'pickup' ? 1 : 0),
            );
            const adaptedRoute = sortedRouteSteps
              .map(step => {
                if (step.type === 'pickup' || step.type === 'delivery') {
                  return {
                    index: step.id,
                    min_time: step.arrival + step.waiting_time - startOfDayUnix,
                    max_time: step.arrival + step.waiting_time - startOfDayUnix,
                  };
                }
                return null;
              })
              .filter(v => !!v);
            return [
              {
                index: startIndex,
                min_time: 0,
                max_time: 0,
              },
              ...adaptedRoute,
              {
                index: requestData.ends[vehicleIndex],
                min_time: 86399,
                max_time: 86399,
              },
            ];
          },
        );

        const parsedRoutes = adaptedVroomRoutes.map((route, index) => {
          return {
            id: chance.guid(),
            stops: route.map((stop, index) => ({
              ...stop,
              ...dayAllStops[stop.index],
              minDate: moment()
                .set('day', selectedDay)
                .startOf('day')
                .add(stop.min_time, 'seconds')
                .toDate(),
              maxDate: moment()
                .set('day', selectedDay)
                .startOf('day')
                .add(stop.max_time, 'seconds')
                .toDate(),
              distanceTo: 0,
            })),
            totalTime: route.reduce(
              (totalTime, stop) => totalTime + stop.min_time,
              0,
            ),
            vehicle: vehicles[index],
            color: vehicles[index].color,
          };
        });

        const fulfilledStops = flatten(parsedRoutes.map(route => route.stops));
        const fulfilledStopIds = fulfilledStops.map(stop => stop.id);
        const allStopIds = dayAllStops.map(stop => stop.id);
        const unfulfilledStopIds = difference(allStopIds, fulfilledStopIds);

        const unfulfilledStops = unfulfilledStopIds.map(id =>
          dayAllStops.find(s => s.id === id),
        );

        const unfulfilledDemandIds = uniq(
          flatten(unfulfilledStops.map(stop => stop.demandIds)),
        );

        if (unfulfilledDemandIds.length > 0) {
          alert(
            t(
              'Il y a des arrêts non servis, veuillez consulter la liste des demandes',
            ),
          );
        }

        this.setState({
          routes: this.state.routes.concat(parsedRoutes),
          unfulfilledStops:
            this.state.unfulfilledStops.concat(unfulfilledStops),
          unfulfilledDemandIds,
        });

        const usefulRoutes = parsedRoutes.filter(
          route => route.stops.length > 2,
        );
        const routeTrips = routesToTrips(usefulRoutes, {
          travelRequirements,
        });

        // Calculate statistics
        const routeTripsWithTotal = await Promise.all(
          routeTrips.map(async trip => {
            const { from, to, stops } = trip;
            const allStopsCoordsString = stops
              .map(stop => `${stop.coords.lng},${stop.coords.lat}`)
              .join(';');
            const routeResponse = await fetch(
              `https://osrm.weegolife.com/route/v1/driving/${from.coords.lng},${
                from.coords.lat
              }${
                allStopsCoordsString === '' ? ';' : `;${allStopsCoordsString};`
              }${to.coords.lng},${to.coords.lat}`,
            ).then(response => response.json());
            return {
              ...trip,
              totalDistance: routeResponse.routes[0]?.distance,
              totalDuration: trip.to.max_time - trip.from.max_time,
            };
          }),
        );

        const longestTripDuration = maxBy(
          routeTripsWithTotal,
          'totalDuration',
        )?.totalDuration;
        const longestTripDistance = maxBy(
          routeTripsWithTotal,
          'totalDistance',
        )?.totalDistance;
        const totalTripsDistance = sumBy(routeTripsWithTotal, 'totalDistance');
        const totalTripsDuration = sumBy(routeTripsWithTotal, 'totalDuration');
        const totalDemands = sumBy(
          routeTripsWithTotal,
          route => route.demandIds?.length,
        );
        const meanDistance = totalTripsDistance / routeTripsWithTotal.length;
        const meanDuration = totalTripsDuration / routeTripsWithTotal.length;
        const meanDemands = totalDemands / routeTripsWithTotal.length;

        this.setState({
          selectedTrips: routeTripsWithTotal,
          statistics: {
            longestTripDuration,
            longestTripDistance,
            totalTripsDistance,
            totalTripsDuration,
            meanDistance,
            meanDuration,
            totalDemands,
            meanDemands,
          },
        });

        const firstUsefulRoute = usefulRoutes[0];
        if (firstUsefulRoute) {
          this.focusTrip(routeTripsWithTotal[0]);
        }
      });
    } catch (error) {
      this.setState({
        error,
      });
      return;
    } finally {
      this.setState({
        generating: false,
      });
    }
  };

  openSaveModal = () => {
    this.setState({
      saveModalOpen: true,
    });
  };

  closeSaveModal = () => {
    this.setState({
      saveModalOpen: false,
    });
  };

  openImportModal = () => {
    this.setState({
      importModalOpen: true,
    });
  };

  closeImportModal = () => {
    this.setState({
      importModalOpen: false,
    });
  };

  onImportFinished = (trips, folder) => {
    const { travelRequirements, options, vehicles } = this.state;
    const { saveLocalFile } = this.props;
    const planningDate = getNextDateOfDay(options.selectedDay);
    saveLocalFile(
      JSON.stringify({
        travelRequirements,
        options,
        vehicles,
        trips,
        folder,
        currentTime: new Date(),
      }),
      {
        name: `planner-logs-${planningDate.toLocaleString()}-${folder}.json`,
      },
    );
  };

  updateDepot = updates => {
    const { depot } = this.state;
    this.setState({
      depot: {
        ...depot,
        ...updates,
      },
    });
  };

  onCenterChanged = debounce(latLng => {
    this.setState({
      mapCenter: latLng,
    });
    const { depot } = this.state;
    if (!depot) {
      this.setState({
        depot: {
          name: 'Dépôt',
          coords: clone(latLng),
        },
      });
    }
  }, 500);

  render() {
    const {
      depot,
      travelRequirements,
      selectedRequirement,
      mapCenter,
      generating,
      routes,
      focusedTrip,
      selectedTrips,
      vehicles,
      error,
      saveModalOpen,
      importModalOpen,
      splitMultiPassengerDemands,
      unfulfilledDemandIds,
      options,
      mapViewMode,
      includePlannedDemands,
      statistics,
      isStatsPanelOpen,
    } = this.state;
    const { t } = this.props;
    const {
      longestTripDuration,
      longestTripDistance,
      totalTripsDistance,
      totalTripsDuration,
      meanDistance,
      meanDuration,
      meanDemands,
      totalDemands,
    } = statistics || {};
    return (
      <div style={styles.container}>
        <Segment basic style={styles.controlsContainer}>
          <Header style={styles.screenTitle} as="h1">
            {t('Planification des trajets')}
          </Header>
          {error && (
            <Message error>
              <Message.Header>{t('Une erreur est survenue')}</Message.Header>
              <Message.Content>
                {error.message || error.error || t('Erreur inconnue')}
              </Message.Content>
            </Message>
          )}
          <div style={styles.optionsVehiclesContainer}>
            <PlannerOptions
              options={options}
              onChange={options => {
                this.setState({
                  options,
                });
              }}
              onTmCacheReset={this.resetTmCache}
              legacy={false}
            />

            <PlannerVehiclesManager
              focusedVehicle={focusedTrip ? focusedTrip.vehicle : null}
              style={styles.vehiclesManager}
              onChange={this.updateVehicles}
            />
          </div>
          <Card
            style={{
              ...styles.requirementsManagerContainer,
              ...(mapViewMode === 'DEMANDS' &&
                styles.expandedRequirementsManagerContainer),
            }}
          >
            <Card.Content
              header={
                <div style={styles.demandsCardHeader}>
                  <Header>
                    {t('Demandes')}
                    {mapViewMode === 'DEMANDS' ? (
                      <Icon name="caret up" />
                    ) : (
                      <Icon name="dropdown" />
                    )}
                  </Header>
                  <span style={styles.statsLabel}>
                    {`${travelRequirements?.length} ${t('Demandes')} `}
                    {unfulfilledDemandIds?.length > 0 && (
                      <span style={styles.unfulfilledStatsLabel}>
                        ({unfulfilledDemandIds?.length} non planifiées)
                      </span>
                    )}
                  </span>
                  <Checkbox
                    checked={includePlannedDemands}
                    label={t('Inclure les demandes planifiées')}
                    onChange={(e, { checked }) =>
                      this.setState({
                        includePlannedDemands: checked,
                      })
                    }
                  />
                </div>
              }
              onClick={() =>
                this.setState({
                  mapViewMode: 'DEMANDS',
                })
              }
            />

            {mapViewMode === 'DEMANDS' && (
              <Card.Content style={styles.requirementsManagerCardContent}>
                <TravelRequirementsManager
                  splitMultiPassengerDemands={splitMultiPassengerDemands}
                  selectedDay={options.selectedDay}
                  selectedGroups={options.selectedGroups}
                  selectedTimeRange={options.selectedTimeRange}
                  droppedDemandIds={unfulfilledDemandIds}
                  mapCenter={mapCenter}
                  style={styles.requirementsManager}
                  onChange={this.updateTravelRequirements}
                  onSelect={this.selectRequirement}
                  includePlanned={includePlannedDemands}
                />
              </Card.Content>
            )}

            {mapViewMode === 'DEMANDS' && (
              <Card.Content textAlign="right">
                <Button
                  primary
                  onClick={this.generateRoutes}
                  disabled={generating || !travelRequirements.length}
                >
                  {generating ? t('Génération en cours') : t('Générer trajets')}
                </Button>
              </Card.Content>
            )}
          </Card>

          <div
            style={{
              ...styles.routesDisplaysContainer,
              ...(mapViewMode === 'TRIPS' &&
                styles.expandedRoutesDisplaysContainer),
            }}
          >
            <Card style={styles.tripsListCard}>
              <Card.Content
                header={
                  <div style={styles.demandsCardHeader}>
                    <Header>
                      {t('Trajets')}
                      {mapViewMode === 'TRIPS' ? (
                        <Icon name="caret up" />
                      ) : (
                        <Icon name="dropdown" />
                      )}
                    </Header>
                    <span style={styles.statsLabel}>
                      {selectedTrips?.length} trajets
                    </span>
                  </div>
                }
                onClick={() => this.setState({ mapViewMode: 'TRIPS' })}
              />
              {mapViewMode === 'TRIPS' && (
                <Card.Content>
                  <Card.Description>
                    {map(
                      groupBy(selectedTrips, 'vehicle.id'),
                      (vehicleTrips, vehicleId) => (
                        <RouteDisplay
                          key={vehicleId}
                          trips={vehicleTrips}
                          focusedTrip={focusedTrip}
                          selectedTrips={selectedTrips}
                          onFocus={this.focusTrip}
                          onSelect={this.selectTrip}
                        />
                      ),
                    )}
                  </Card.Description>
                </Card.Content>
              )}
            </Card>
          </div>

          <div style={styles.statsContainer}>
            <Card style={styles.tripsListCard}>
              <Card.Content
                header={
                  <div style={styles.demandsCardHeader}>
                    <Header>
                      {t('Statistiques')}
                      {isStatsPanelOpen ? (
                        <Icon name="caret up" />
                      ) : (
                        <Icon name="dropdown" />
                      )}
                    </Header>
                  </div>
                }
                onClick={() =>
                  this.setState({ isStatsPanelOpen: !isStatsPanelOpen })
                }
              />
              {isStatsPanelOpen && (
                <Card.Content>
                  <Card.Description>
                    {statistics && !isEqual(statistics, {}) && (
                      <div>
                        <Header style={styles.statsTitle} as="h4">
                          {t('Distance')} (km)
                        </Header>
                        <Statistic.Group
                          style={styles.statsDetails}
                          size="mini"
                          color="violet"
                        >
                          <Statistic>
                            <Statistic.Value>
                              {floor(totalTripsDistance / 1000, 2)}
                            </Statistic.Value>
                            <Statistic.Label>{t('Total')}</Statistic.Label>
                          </Statistic>
                          <Statistic>
                            <Statistic.Value>
                              {floor(longestTripDistance / 1000, 2)}
                            </Statistic.Value>
                            <Statistic.Label>
                              {t('Plus longue')}
                            </Statistic.Label>
                          </Statistic>
                          <Statistic>
                            <Statistic.Value>
                              {floor(meanDistance / 1000, 2)}
                            </Statistic.Value>
                            <Statistic.Label>{t('Moyenne')}</Statistic.Label>
                          </Statistic>
                        </Statistic.Group>
                        <Divider />
                        <Header style={styles.statsTitle} as="h4">
                          {t('Durée')} (min)
                        </Header>
                        <Statistic.Group
                          style={styles.statsDetails}
                          size="mini"
                          color="violet"
                        >
                          <Statistic>
                            <Statistic.Value>
                              {Math.ceil(totalTripsDuration / 60)}
                            </Statistic.Value>
                            <Statistic.Label>{t('Total')}</Statistic.Label>
                          </Statistic>
                          <Statistic>
                            <Statistic.Value>
                              {Math.ceil(longestTripDuration / 60)}
                            </Statistic.Value>
                            <Statistic.Label>
                              {t('Plus longue')}
                            </Statistic.Label>
                          </Statistic>
                          <Statistic>
                            <Statistic.Value>
                              {Math.ceil(meanDuration / 60)}
                            </Statistic.Value>
                            <Statistic.Label>{'Moyenne'}</Statistic.Label>
                          </Statistic>
                        </Statistic.Group>
                        <Divider />
                        <Header style={styles.statsTitle} as="h4">
                          {t('Occupation')} ({t('place')})
                        </Header>
                        <Statistic.Group
                          style={styles.statsDetails}
                          size="mini"
                          color="violet"
                        >
                          <Statistic>
                            <Statistic.Value>{totalDemands}</Statistic.Value>
                            <Statistic.Label>{t('Total')}</Statistic.Label>
                          </Statistic>
                          <Statistic>
                            <Statistic.Value>
                              {floor(meanDemands, 2)}
                            </Statistic.Value>
                            <Statistic.Label>{t('Moyenne')}</Statistic.Label>
                          </Statistic>
                        </Statistic.Group>
                      </div>
                    )}
                  </Card.Description>
                </Card.Content>
              )}
            </Card>
          </div>
        </Segment>
        <Segment basic style={styles.routesContainer}>
          <div style={styles.routesActions}>
            <Button
              onClick={this.openImportModal}
              disabled={generating}
              primary
              inverted
            >
              <Icon name="download" />
              {t('Importer')}
            </Button>
            <Button
              primary
              onClick={this.openSaveModal}
              disabled={generating || !routes.length}
              loading={false}
            >
              <Icon name="save" />
              {t('Enregistrer')}
            </Button>
          </div>
          <div style={styles.mapContainer}>
            {mapViewMode === 'DEMANDS' && (
              <Map
                style={styles.map}
                onCenterChanged={() => {
                  if (mapRef.current) {
                    this.onCenterChanged(mapRef.current.getCenter().toJSON());
                  }
                }}
                onTilesLoaded={() => {
                  if (mapRef.current) {
                    this.onCenterChanged(mapRef.current.getCenter().toJSON());
                  }
                }}
                onClick={() => {
                  this.dismissSelectedRequirement();
                  this.dismissFocusedTrip();
                }}
                defaultZoom={14}
                ref={mapRef}
              >
                {travelRequirements.map((requirement, index) => (
                  <TravelRequirementEditor
                    key={index}
                    requirement={requirement}
                    readonly={
                      !selectedRequirement ||
                      requirement.id !== selectedRequirement.id
                    }
                    onChange={this.updateSelectedRequirement}
                    onSelect={this.selectRequirement}
                    onDismiss={this.dismissSelectedRequirement}
                  />
                ))}
                {depot && (
                  <Marker
                    position={depot.coords}
                    draggable={true}
                    label={t('Dépôt')}
                    onDragEnd={event =>
                      this.updateDepot({ coords: event.latLng.toJSON() })
                    }
                  />
                )}
                {vehicles
                  .filter(vehicle => vehicle.available)
                  .filter(vehicle => vehicle.depot)
                  .map(vehicle => (
                    <Marker
                      key={vehicle.id}
                      position={vehicle.depot.coords}
                      icon={{
                        path: busIconPath,
                        fillColor: vehicle.color,
                        fillOpacity: 1,
                        strokeColor: 'white',
                        strokeWeight: 1,
                        strokeOpacity: 1,
                        scale: 0.1,
                        anchor: { x: 212.5, y: 470 },
                      }}
                    ></Marker>
                  ))}
              </Map>
            )}
            {mapViewMode === 'TRIPS' && <VehiclesMap />}
          </div>
        </Segment>
        <Modal open={saveModalOpen} onClose={this.closeSaveModal} size="large">
          <Header icon="browser" content={t('Enregistrer les trajets')} />
          <Modal.Content>
            <RoutesSaver
              routes={routes}
              selectedTrips={selectedTrips}
              onImportFinished={this.onImportFinished}
            />
          </Modal.Content>
        </Modal>
        <Modal
          open={importModalOpen}
          onClose={this.closeImportModal}
          size="large"
        >
          <Header icon="browser" content={t('Importer/Exporter')} />
          <Modal.Content>
            <RoutesImporter
              routes={routes}
              onImport={this.importRoutes}
              defaultDepot={depot}
            />
          </Modal.Content>
        </Modal>
      </div>
    );
  }
}

const styles = {
  container: {
    height: 'calc(100% - 80px)',
    display: 'flex',
    flexDirection: 'row',
  },
  screenTitle: {
    paddingBottom: 10,
  },
  routesContainer: {
    flex: 4,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  routesActions: {
    alignSelf: 'flex-end',
    paddingBottom: 20,
  },
  mapContainer: {
    flex: 1,
  },
  routesDisplaysContainer: {
    overflow: 'auto',
    flex: 1,
  },
  expandedRoutesDisplaysContainer: {
    height: '40%',
  },
  controlsContainer: {
    height: '100%',
    overflow: 'auto',
    flex: 3,
    margin: 0,
  },
  optionsVehiclesContainer: {
    display: 'flex',
  },
  vehiclesManager: {
    // 115px = height of header + generate button + card margin
    height: 'calc((100% - 115px) / 2)',
    marginTop: 0,
  },
  demandsCardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  requirementsManagerContainer: {
    width: '100%',
  },
  expandedRequirementsManagerContainer: {
    height: '40%',
  },
  requirementsManagerCardContent: {
    // 110px = height of header + footer
    height: 'calc(100% - 110px)',
  },
  requirementsManager: {
    height: '100%',
    overflow: 'auto',
  },
  tripsListCard: {
    width: '100%',
    margin: 0,
    cursor: 'pointer',
    marginTop: 10,
  },
  statsLabel: { fontSize: 16 },
  unfulfilledStatsLabel: { color: 'red', fontWeight: 'bold' },
  statsTitle: { textAlign: 'center' },
  statsDetails: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  statsContainer: { overflowX: 'hidden', overflowY: 'hidden' },
};

export default withi18n('planner')(AutomaticPlannerScreen);

const latLngToCoordinates = latLng => ({
  latitude: latLng.lat,
  longitude: latLng.lng,
});
